import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable } from 'rxjs';
import { MembersDomainModel } from '../../../../domainModels/members-domain-model';
import { MemberUserSearchResult } from '../../../../models/users/dto/member-user-search-result';
import { OdataTypeaheadSearchComponent } from '../odata-typeahead-search/odata-typeahead-search.component';

@Component({
  selector: 'app-member-search-input',
  templateUrl: './member-search-input.component.html',
  styleUrls: ['./member-search-input.component.scss'],
  standalone: true,
  imports: [OdataTypeaheadSearchComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MemberSearchInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MemberSearchInputComponent implements ControlValueAccessor, OnChanges {
  @Input() employerId?: number;
  @Input() label = '';
  @Input() placeHolder = $localize`Search by name or member ID`;
  @Input() controlId = 'memberSearch';
  @Input() hasErrors = false;
  @Input() searchByNameOnly = false;

  private membersDomainModel = inject(MembersDomainModel);
  private cdr = inject(ChangeDetectorRef);

  public disabled = false;
  public selectedMember: MemberUserSearchResult | null = null;
  public searchedProperties = ['name', 'id'];

  private onChange: (value: MemberUserSearchResult | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  public odataFunction = (searchString: string): Observable<MemberUserSearchResult[]> => {
    return this.searchByNameOnly
      ? this.membersDomainModel.searchMembersByName(searchString, this.employerId)
      : this.membersDomainModel.searchMembersByIdOrName(searchString, this.employerId);
  };

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.searchByNameOnly) {
      this.searchedProperties = this.searchByNameOnly ? ['name'] : ['name', 'id'];
    }
  }

  public onMemberSelected(member: MemberUserSearchResult): void {
    this.selectedMember = member;
    this.onChange(member);
    this.onTouched();
    this.cdr.markForCheck();
  }

  public writeValue(value: MemberUserSearchResult | null): void {
    this.selectedMember = value;
    this.cdr.markForCheck();
  }

  public registerOnChange(fn: (value: MemberUserSearchResult | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }
}
